import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Building2, Plus, Users, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const WorkspaceManagement = ({ user, setUser }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    admin_email: '',
    admin_name: '',
    admin_password: ''
  });

  useEffect(() => {
    if (user.is_super_admin) {
      fetchWorkspaces();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchWorkspaces = async () => {
    try {
      const res = await axios.get(`${API_URL}/workspaces`);
      setWorkspaces(res.data);
    } catch (error) {
      toast.error('Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      await axios.post(`${API_URL}/workspaces`, formData);
      toast.success('Workspace created successfully!');
      setDialogOpen(false);
      setFormData({
        name: '',
        subdomain: '',
        admin_email: '',
        admin_name: '',
        admin_password: ''
      });
      fetchWorkspaces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create workspace');
    } finally {
      setCreating(false);
    }
  };

  if (!user.is_super_admin) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Access Denied</h2>
            <p className="text-zinc-600">Only super admins can manage workspaces</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="p-8" data-testid="workspace-management">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                Workspace Management
              </h1>
              <p className="text-zinc-600" style={{ fontFamily: 'Inter, sans-serif' }}>
                Create and manage client workspaces
              </p>
            </div>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-violet-600 hover:bg-violet-700" data-testid="create-workspace-button">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workspace
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Create New Workspace</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <Label>Workspace Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Acme Corp Knowledge Base"
                      required
                      className="mt-2"
                      data-testid="workspace-name-input"
                    />
                  </div>
                  <div>
                    <Label>Subdomain</Label>
                    <Input
                      value={formData.subdomain}
                      onChange={(e) => setFormData({...formData, subdomain: e.target.value})}
                      placeholder="acme-corp"
                      required
                      className="mt-2"
                      data-testid="workspace-subdomain-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">This will be used for identification</p>
                  </div>
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-zinc-900 mb-3">Admin User</h4>
                    <div className="space-y-3">
                      <div>
                        <Label>Admin Name</Label>
                        <Input
                          value={formData.admin_name}
                          onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                          placeholder="John Doe"
                          required
                          className="mt-2"
                          data-testid="admin-name-input"
                        />
                      </div>
                      <div>
                        <Label>Admin Email</Label>
                        <Input
                          type="email"
                          value={formData.admin_email}
                          onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                          placeholder="admin@acme.com"
                          required
                          className="mt-2"
                          data-testid="admin-email-input"
                        />
                      </div>
                      <div>
                        <Label>Admin Password</Label>
                        <Input
                          type="password"
                          value={formData.admin_password}
                          onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                          placeholder="Secure password"
                          required
                          className="mt-2"
                          data-testid="admin-password-input"
                        />
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={creating} data-testid="submit-workspace-button">
                    {creating ? 'Creating...' : 'Create Workspace'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Loading workspaces...</div>
        ) : workspaces.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="workspaces-grid">
            {workspaces.map((workspace) => (
              <div
                key={workspace.id}
                className="bg-white border border-zinc-200 rounded-lg p-6 hover:border-violet-300 hover:shadow-sm transition-all duration-200"
                data-testid={`workspace-card-${workspace.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="h-12 w-12 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-violet-600" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {workspace.name}
                </h3>
                <div className="space-y-2 text-sm text-zinc-600">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-zinc-100 px-2 py-1 rounded">{workspace.subdomain}</span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    Created {new Date(workspace.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12" data-testid="empty-state">
            <Building2 className="h-16 w-16 mx-auto mb-4 text-zinc-300" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No workspaces yet</h3>
            <p className="text-zinc-600 mb-6">Create your first workspace for a client</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-violet-600 hover:bg-violet-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Workspace
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default WorkspaceManagement;